import { freddyTheme } from '@coolblue-development/freddy';
import { CheckmarkCircled } from '@coolblue-development/icons';
import { AvailabilityState as ClientAvailabilityState } from '@coolblue-development/next-availability-state/client';
import { useFormatShortDate } from '@coolblue-development/next-formatting-utils/client';
import { AvailabilityState } from '@features/product/typings';
import { useTranslation } from '@i18n/useTranslation';

type Props = {
  availabilityState: AvailabilityState;
  stockLocationId: number;
  isClosed: boolean;
  isAvailableOnline: boolean;
  openingTime: string;
  releaseDate?: string;
  dateOverride?: string;
};

const FRANKFURT_STOCK_LOCATION_ID = 412;
const FRANKFURT_ACCEPT_TIME = 21;
const FRIDAY_DAYOFWEEK = 5;
const SATURDAY_DAYOFWEEK = 6;
const AMSTERDAM_TIMEZONE = 'Europe/Amsterdam';

const getCurrentDateTimeInTimeZone = (
  timeZone: string,
  date: Date
): { hourOfDay: number; dayOfWeek: number } => {
  const currentDate = new Date(date.toLocaleString('en-US', { timeZone }));
  return {
    hourOfDay: currentDate.getHours(),
    dayOfWeek: currentDate.getDay()
  };
};

function StoreAvailabilityState({
  availabilityState,
  stockLocationId,
  isClosed,
  isAvailableOnline,
  openingTime,
  releaseDate,
  dateOverride
}: Props) {
  const { t } = useTranslation('webshop.filterPage.storePickup.propositions');
  const formattedReleaseDate = useFormatShortDate(releaseDate ? new Date(releaseDate) : new Date());

  const isFrankfurt = Number(stockLocationId) === FRANKFURT_STOCK_LOCATION_ID;
  const date = dateOverride ? new Date(dateOverride) : new Date();
  const { hourOfDay, dayOfWeek } = getCurrentDateTimeInTimeZone(AMSTERDAM_TIMEZONE, date);
  const isFriday = dayOfWeek === FRIDAY_DAYOFWEEK;
  const isSaturday = dayOfWeek === SATURDAY_DAYOFWEEK;
  const isAfterFrankfurtAcceptTime = hourOfDay >= FRANKFURT_ACCEPT_TIME;

  const getAvailabilityStateProps = () => {
    switch (availabilityState) {
      case AvailabilityState.OnStock:
        return {
          description: isClosed ? t('nextDayPickup', { openingTime }) : t('directPickup'),
          promise: t('onStock'),
          state: {
            color: freddyTheme.color.font.sentimentPositiveContrast,
            icon: <CheckmarkCircled color={freddyTheme.color.icon.sentimentPositiveContrast} />
          }
        };
      case AvailabilityState.PreOrder:
        return {
          preOrderPromise: t('preOrderPickup', { openingTime, releaseDate: formattedReleaseDate }),
          promise: t('preOrder'),
          state: {
            color: freddyTheme.color.font.sentimentNegative
          }
        };
      case AvailabilityState.TemporaryOutOfStock:
        if (isFrankfurt && (isAfterFrankfurtAcceptTime || isSaturday)) {
          return {
            description: t(isFriday || isSaturday ? 'storePickupMonday' : 'dayAfterPickup', {
              openingTime
            }),
            promise: t('temporaryOutOfStock'),
            state: {
              color: freddyTheme.color.font.sentimentNegative
            }
          };
        }

        return {
          description: isAvailableOnline
            ? t('nextDayPickup', { openingTime })
            : t('notAvailableForPickup'),
          promise: t('temporaryOutOfStock'),
          state: {
            color: freddyTheme.color.font.sentimentNegative
          }
        };
      default:
        return {
          description: t('notAvailableForPickup'),
          promise: t('temporaryOutOfStock'),
          state: {
            color: freddyTheme.color.font.sentimentNegative
          }
        };
    }
  };

  return <ClientAvailabilityState {...getAvailabilityStateProps()} />;
}

export default StoreAvailabilityState;
